/**
 * Chat Service - SSE-based streaming chat for web
 * Uses Server-Sent Events for streaming responses from the backend
 */

import { authService } from './authService';
import { logger } from '../utils/logger';

// Uses same env var name as Electron version (with VITE_ prefix)
const API_BASE_URL = import.meta.env.VITE_CORPUS_API_BASE_URL || '';

export type ChatEventType =
  | 'conversation_started'
  | 'conversation_updated'
  | 'token'
  | 'thinking'
  | 'tool_start'
  | 'tool_end'
  | 'tool_confirmation'
  | 'stream_resumed'
  | 'final'
  | 'cancelled'
  | 'error'
  | 'connection_status';

export interface ConversationStartedEvent {
  conversationId: string;
  conversationName?: string;
}

export interface ConversationUpdatedEvent {
  conversationId: string;
  name: string;
}

export interface TokenEvent {
  token: string;
  conversationId?: string;
}

export interface ThinkingEvent {
  content: string;
  conversationId?: string;
}

export interface ToolStartEvent {
  toolName: string;
  toolDisplayName?: string;
  args: Record<string, unknown>;
}

export interface ToolEndEvent {
  resultPreview: string;
}

export interface ToolConfirmationEvent {
  executionId: string;
  toolName: string;
  toolArgs: Record<string, unknown>;
}

export interface StreamResumedEvent {
  toolResult: string;
}

export interface FinalEvent {
  conversationId?: string;
  messageId?: string;
  content: string;
  timestamp: string;
  generatedContract?: {
    content_b64: string;
    filename: string;
    media_type: string;
    size_bytes: number;
  } | null;
}

export interface ErrorEvent {
  error: string;
  conversationId?: string;
}

export interface ConnectionStatusEvent {
  status: 'connecting' | 'connected' | 'disconnected' | 'streaming' | 'error';
  error?: string;
}

export interface CancelledEvent {
  message: string;
}

type ChatEventCallback<T> = (data: T) => void;

export interface ChatEventHandlers {
  onConversationStarted?: ChatEventCallback<ConversationStartedEvent>;
  onConversationUpdated?: ChatEventCallback<ConversationUpdatedEvent>;
  onToken?: ChatEventCallback<TokenEvent>;
  onThinking?: ChatEventCallback<ThinkingEvent>;
  onToolStart?: ChatEventCallback<ToolStartEvent>;
  onToolEnd?: ChatEventCallback<ToolEndEvent>;
  onToolConfirmation?: ChatEventCallback<ToolConfirmationEvent>;
  onStreamResumed?: ChatEventCallback<StreamResumedEvent>;
  onFinal?: ChatEventCallback<FinalEvent>;
  onCancelled?: ChatEventCallback<CancelledEvent>;
  onError?: ChatEventCallback<ErrorEvent>;
  onConnectionStatus?: ChatEventCallback<ConnectionStatusEvent>;
}

/**
 * Chat Service - Manages SSE connection for streaming chat
 */
export class ChatService {
  private handlers: ChatEventHandlers = {};
  private abortController: AbortController | null = null;
  private currentConversationId: string | null = null;

  constructor() {}

  /**
   * Set event handlers
   */
  setHandlers(handlers: ChatEventHandlers): void {
    this.handlers = { ...this.handlers, ...handlers };
  }

  /**
   * Subscribe to a conversation (for SSE, this is a no-op - we stream on send)
   */
  async subscribe(conversationId: string): Promise<{ success: boolean; error?: string }> {
    this.currentConversationId = conversationId;
    return { success: true };
  }

  /**
   * Unsubscribe from current conversation
   */
  async unsubscribe(): Promise<{ success: boolean; error?: string }> {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
    this.currentConversationId = null;
    return { success: true };
  }

  /**
   * Send a message and stream the response via SSE
   */
  async send(input: {
    conversationId?: string;
    content: string;
    corpusId?: string;
    building_contract_id?: number;
    deep_web_search?: boolean;
    attached_file_keys?: string[];
  }): Promise<{ success: boolean; conversationId?: string; error?: string }> {
    try {
      const token = authService.getToken();
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }

      // Abort any existing stream
      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      this.handlers.onConnectionStatus?.({ status: 'connecting' });

      const requestBody: Record<string, unknown> = {
        text: input.content
      };

      if (input.corpusId) {
        requestBody.corpus_id = input.corpusId;
      }

      if (input.conversationId) {
        requestBody.conversation_id = input.conversationId;
      }
      if (input.building_contract_id) {
        requestBody.building_contract_id = input.building_contract_id;
        requestBody.is_contract_builder = true;
      }
      if (input.deep_web_search) {
        requestBody.deep_web_search = true;
      }
      if (input.attached_file_keys && input.attached_file_keys.length > 0) {
        requestBody.attached_file_keys = input.attached_file_keys;
      }

      logger.log('[ChatService] Sending request:', requestBody);

      const response = await fetch(`${API_BASE_URL}/api/protected/chat/stream`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'text/event-stream'
        },
        body: JSON.stringify(requestBody),
        signal: this.abortController.signal
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        logger.error('[ChatService] Stream error:', response.status, errorData);
        this.handlers.onConnectionStatus?.({ status: 'error', error: errorData.detail || 'Stream failed' });
        return { success: false, error: errorData.detail || 'Failed to start stream' };
      }

      this.handlers.onConnectionStatus?.({ status: 'streaming' });

      // Process SSE stream
      const reader = response.body?.getReader();
      if (!reader) {
        return { success: false, error: 'No response body' };
      }

      const decoder = new TextDecoder();
      let buffer = '';
      let conversationId = input.conversationId;

      try {
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              const jsonStr = line.slice(6).trim();
              if (jsonStr === '[DONE]') {
                continue;
              }

              try {
                const event = JSON.parse(jsonStr);
                conversationId = this.handleSSEEvent(event, conversationId);
              } catch (parseError) {
                logger.warn('Failed to parse SSE event:', jsonStr);
              }
            }
          }
        }
      } catch (error: any) {
        if (error.name === 'AbortError') {
          // Stream was aborted intentionally
          return { success: true, conversationId };
        }
        throw error;
      }

      this.handlers.onConnectionStatus?.({ status: 'connected' });
      return { success: true, conversationId };

    } catch (error: any) {
      if (error.name === 'AbortError') {
        return { success: true, conversationId: input.conversationId };
      }
      this.handlers.onConnectionStatus?.({ status: 'error', error: error.message });
      return { success: false, error: error.message };
    }
  }

  /**
   * Handle SSE event and dispatch to handlers
   */
  private handleSSEEvent(event: any, currentConversationId?: string): string | undefined {
    const eventType = event.type || event.event;

    switch (eventType) {
      case 'conversation_started':
      case 'conversation_created':
        logger.log('[ChatService] Conversation created/started:', event.conversation_id);
        this.handlers.onConversationStarted?.({
          conversationId: event.conversation_id,
          conversationName: event.conversation_name || event.name
        });
        return event.conversation_id;

      case 'conversation_updated':
        this.handlers.onConversationUpdated?.({
          conversationId: event.conversation_id,
          name: event.name
        });
        break;

      case 'token':
        this.handlers.onToken?.({
          token: event.token || event.content || '',
          conversationId: event.conversation_id
        });
        break;

      case 'thinking':
        this.handlers.onThinking?.({
          content: event.content || event.message || '',
          conversationId: event.conversation_id
        });
        break;

      case 'tool_start':
        this.handlers.onToolStart?.({
          toolName: event.tool_name,
          toolDisplayName: event.tool_display_name,
          args: event.args || {}
        });
        break;

      case 'tool_end':
        this.handlers.onToolEnd?.({
          resultPreview: event.result_preview || event.result || ''
        });
        break;

      case 'tool_confirmation':
        this.handlers.onToolConfirmation?.({
          executionId: event.execution_id,
          toolName: event.tool_name,
          toolArgs: event.tool_args || {}
        });
        break;

      case 'stream_resumed':
        this.handlers.onStreamResumed?.({
          toolResult: event.tool_result
        });
        break;

      case 'final':
      case 'done':
        this.handlers.onFinal?.({
          conversationId: event.conversation_id,
          messageId: event.message_id,
          content: event.content || '',
          timestamp: event.timestamp || new Date().toISOString(),
          generatedContract: event.generated_contract
        });
        break;

      case 'cancelled':
        this.handlers.onCancelled?.({
          message: event.message || 'Generation cancelled'
        });
        break;

      case 'error':
        this.handlers.onError?.({
          error: event.error || event.message || 'Unknown error',
          conversationId: event.conversation_id
        });
        break;

      default:
        // Handle raw token streaming (some backends send tokens without type)
        if (event.token || event.content) {
          this.handlers.onToken?.({
            token: event.token || event.content,
            conversationId: event.conversation_id
          });
        }
    }

    return currentConversationId;
  }

  /**
   * Send tool response
   */
  async sendToolResponse(response: {
    executionId: string;
    success: boolean;
    result?: unknown;
    error?: string;
  }): Promise<{ success: boolean; error?: string }> {
    try {
      const token = authService.getToken();
      if (!token) {
        return { success: false, error: 'Not authenticated' };
      }

      const apiResponse = await fetch(`${API_BASE_URL}/api/protected/chat/tool-response`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(response)
      });

      if (!apiResponse.ok) {
        return { success: false, error: 'Failed to send tool response' };
      }

      return { success: true };
    } catch (error: any) {
      return { success: false, error: error.message };
    }
  }

  /**
   * Get current status
   */
  getStatus(): { status: string; subscribedConversationId: string | null } {
    return {
      status: this.abortController ? 'streaming' : 'idle',
      subscribedConversationId: this.currentConversationId
    };
  }
}

// Default instance
export const chatService = new ChatService();
